/*
 * FURVIBE — "Recently viewed" rail (sections/fv-recently-viewed.liquid).
 * 1. Records the product on screen in localStorage 'fv:recent' (handles, newest first, unique, max 12).
 * 2. Shows up to N products viewed before it (never the current one), hidden when fewer than 2.
 *    Cards are the real Liquid card (snippets/fv-card): this section, requested for another product
 *    through the Section Rendering API, returns that product's card in <template data-fv-rv-card>.
 *    If that fails (e.g. the product uses another template), /products/<handle>.js is used and a
 *    card with the same markup and classes is built here.
 * 3. Cards are cached in sessionStorage for 5 minutes, so the rail is instant while browsing.
 * 4. "Clear" empties the list (with Undo). The section takes no space until cards are ready.
 */

const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
const LIST_KEY = 'fv:recent';
const CACHE_KEY = 'fv:recent:cards';
const MAX_STORED = 12;
const MIN_SHOWN = 2;
const CACHE_TTL = 5 * 60 * 1000;
const SIZES = '(min-width: 990px) 22vw, (min-width: 750px) 44vw, 64vw';
const WIDTHS = [320, 480, 640, 800, 1000];
/* Same alt-text hints as snippets/fv-card: studio shots sit cut-out on the tile. */
const CUTOUT_HINTS = ['white background', 'on white', 'product view', 'folded', 'upright', 'top view', 'flat'];

const reduceMotion = () => !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
const designMode = () => !!(window.Shopify && window.Shopify.designMode);

/* ----------------------------------------------------------------- storage */
function store(area) {
  try {
    return window[area] || null;
  } catch (e) {
    return null;
  }
}

function readJSON(area, key) {
  try {
    const s = store(area);
    const raw = s && s.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
}

function writeJSON(area, key, value) {
  try {
    const s = store(area);
    if (s) s.setItem(key, JSON.stringify(value));
  } catch (e) {
    /* Full, blocked or private mode: the rail simply doesn't remember. */
  }
}

function removeKey(area, key) {
  try {
    const s = store(area);
    if (s) s.removeItem(key);
  } catch (e) {
    /* ignore */
  }
}

function readList() {
  const raw = readJSON('localStorage', LIST_KEY);
  if (!Array.isArray(raw)) return [];
  const seen = new Set();
  const out = [];
  for (const h of raw) {
    if (typeof h !== 'string' || !h || h.length > 255 || seen.has(h)) continue;
    seen.add(h);
    out.push(h);
    if (out.length === MAX_STORED) break;
  }
  return out;
}

function writeList(list) {
  writeJSON('localStorage', LIST_KEY, list.slice(0, MAX_STORED));
}

function readCache(currency) {
  const c = readJSON('sessionStorage', CACHE_KEY);
  return c && c.c === currency && c.items && typeof c.items === 'object' ? c : { c: currency, items: {} };
}

function writeCache(cache) {
  const keep = Object.entries(cache.items)
    .filter(([, v]) => v && typeof v.h === 'string' && typeof v.t === 'number')
    .sort((a, b) => b[1].t - a[1].t)
    .slice(0, MAX_STORED + 4);
  writeJSON('sessionStorage', CACHE_KEY, { c: cache.c, items: Object.fromEntries(keep) });
}

/* ------------------------------------------------- fallback card (from .js) */
const ESC = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ESC[c]);

function sized(src, width) {
  try {
    const u = new URL(src, window.location.href);
    u.searchParams.set('width', String(width));
    return u.href;
  } catch (e) {
    return src;
  }
}

function imgTag(media, alt, cls) {
  const pi = media.preview_image || media;
  const src = pi.src || media.src;
  if (!src) return '';
  const srcset = WIDTHS.map((w) => `${sized(src, w)} ${w}w`).join(', ');
  const dims = pi.width && pi.height ? ` width="${pi.width}" height="${pi.height}"` : '';
  return `<img class="${cls}" src="${esc(sized(src, 800))}" srcset="${esc(srcset)}" sizes="${SIZES}" loading="lazy" alt="${esc(alt)}"${dims}>`;
}

/* Mirrors snippets/fv-card (minus colour dots, which need the Liquid swatch map). */
function buildCard(p, handle, money) {
  const variants = p.variants || [];
  const images = (p.media || []).filter((m) => m && m.media_type === 'image');
  const byId = (id) => images.find((m) => m.id === id);
  const attached = new Set(variants.map((v) => v.featured_media && v.featured_media.id).filter(Boolean));

  const lead = variants.find((v) => v.available) || variants[0];
  let m1 = lead && lead.featured_media ? byId(lead.featured_media.id) || lead.featured_media : null;
  if (!m1) m1 = images[0] || (p.featured_image ? { src: p.featured_image, alt: '' } : null);
  let m2 = m1 ? images.find((m) => m.id !== m1.id && !attached.has(m.id)) : null;
  if (!m2 && m1) m2 = images.find((m) => m.id !== m1.id) || null;
  const alt = String((m1 && m1.alt) || '').toLowerCase();
  const cutout = !!m1 && CUTOUT_HINTS.some((w) => alt.includes(w));

  /* "From" price: in single-option products where most values read "A / B", values without the
     slash are add-ons (e.g. a spatula) and must not set it. */
  let from = p.price_min;
  let varies = !!p.price_varies;
  if ((p.options || []).length === 1 && variants.length > 1) {
    const paired = variants.filter((v) => String(v.option1 || '').includes(' / '));
    if (paired.length > 0 && paired.length < variants.length) {
      const prices = paired.map((v) => v.price);
      from = Math.min(...prices);
      varies = Math.max(...prices) > from;
    }
  }
  const was = p.compare_at_price_min > p.price_min ? ` <s class="fv-card__was">${esc(money(p.compare_at_price_min))}</s>` : '';

  let badge = '';
  if (!p.available) badge = '<span class="fv-card__badge">Sold out</span>';
  else if ((p.tags || []).includes('merch:bestseller')) badge = '<span class="fv-card__badge fv-card__badge--sig">Signature</span>';

  const mediaCls = `fv-card__media${m2 ? ' fv-card__media--swap' : ''}${cutout ? ' fv-card__media--cutout' : ''}`;
  const url = `${ROOT}products/${encodeURIComponent(p.handle || handle)}`;
  return (
    `<li class="fv-card"><a class="fv-card__link" href="${esc(url)}">` +
    `<span class="${mediaCls}">` +
    (m1 ? imgTag(m1, p.title, 'fv-card__img') : '<span class="fv-card__img fv-card__ph"></span>') +
    (m2 ? imgTag(m2, '', 'fv-card__img fv-card__img--2') : '') +
    badge +
    '</span><span class="fv-card__body">' +
    `<span class="fv-card__name">${esc(p.title)}</span>` +
    `<span class="fv-card__price">${varies ? '<span class="fv-card__from">From</span> ' : ''}${esc(money(from))}${was}</span>` +
    '</span></a></li>'
  );
}

/* ---------------------------------------------------------------- element */
function fade(el, from, to, ms) {
  if (!el || reduceMotion() || typeof el.animate !== 'function') return Promise.resolve();
  const a = el.animate([{ opacity: from }, { opacity: to }], { duration: ms, easing: 'cubic-bezier(.2,.7,.2,1)', fill: 'both' });
  return a.finished.then(() => a).catch(() => a);
}

function cancelFades(el) {
  if (el && typeof el.getAnimations === 'function') el.getAnimations().forEach((a) => a.cancel());
}

class FvRecentlyViewed extends HTMLElement {
  connectedCallback() {
    if (!this._ready) this.setup();
    this.live = new AbortController();
    window.addEventListener(
      'pageshow',
      (e) => {
        // Back/forward cache: the shopper may have viewed other products since this page was built.
        if (e.persisted) this.refresh();
      },
      { signal: this.live.signal }
    );
    if ('ResizeObserver' in window) {
      this.ro = new ResizeObserver(() => this.updateRail());
      this.ro.observe(this.$list);
    }
    this.refresh();
  }

  disconnectedCallback() {
    if (this.live) this.live.abort();
    if (this.loading) this.loading.abort();
    if (this.ro) this.ro.disconnect();
    if (this.io) this.io.disconnect();
  }

  setup() {
    this._ready = true;
    this.handle = this.dataset.handle || '';
    this.sectionId = this.dataset.sectionId || '';
    this.limit = Math.min(8, Math.max(MIN_SHOWN, parseInt(this.dataset.limit, 10) || 4));
    this.currency =
      (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || this.dataset.currency || 'USD';
    let fmt;
    try {
      fmt = new Intl.NumberFormat('en-US', { style: 'currency', currency: this.currency });
    } catch (e) {
      fmt = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });
    }
    this.money = (cents) => fmt.format((Number(cents) || 0) / 100);

    const q = (s) => this.querySelector(s);
    this.$list = q('[data-fv-rv-list]');
    this.$body = q('[data-fv-rv-body]');
    this.$nav = q('[data-fv-rv-nav]');
    this.$bar = q('[data-fv-rv-bar]');
    this.$thumb = q('[data-fv-rv-thumb]');
    this.$clear = q('[data-fv-rv-clear]');
    this.$cleared = q('[data-fv-rv-cleared]');
    this.$undo = q('[data-fv-rv-undo]');
    this.$status = q('[data-fv-rv-status]');
    this.$editor = q('[data-fv-rv-editor]');

    // The card of the product on screen is always fresh: keep it, so later pages show it instantly.
    const tpl = q('template[data-fv-rv-card]');
    const own = tpl && tpl.content.querySelector('.fv-card');
    this.ownCard = own ? own.outerHTML : '';

    let frame = 0;
    this.$list.addEventListener(
      'scroll',
      () => {
        if (frame) return;
        frame = requestAnimationFrame(() => {
          frame = 0;
          this.updateRail();
        });
      },
      { passive: true }
    );
    this.querySelectorAll('[data-fv-rv-step]').forEach((btn) => {
      btn.addEventListener('click', () => this.step(Number(btn.dataset.fvRvStep) || 1));
    });
    if (this.$clear) this.$clear.addEventListener('click', () => this.clear());
    if (this.$undo) this.$undo.addEventListener('click', () => this.undo());
  }

  /* Move this product to the front of the list; return what was viewed before it. */
  record() {
    const before = readList().filter((h) => h !== this.handle);
    if (this.handle) writeList([this.handle, ...before]);
    return before;
  }

  async refresh() {
    if (this.loading) this.loading.abort();
    const ac = (this.loading = new AbortController());
    const before = this.record();
    const cards = await this.collect(before, ac.signal);
    if (ac.signal.aborted) return;
    this.render(cards);
  }

  async collect(handles, signal) {
    const now = Date.now();
    const cache = readCache(this.currency);
    if (this.ownCard && this.handle) cache.items[this.handle] = { t: now, h: this.ownCard };
    const found = new Map();
    const gone = [];
    let i = 0;
    // Fill the rail in order; a product that no longer exists frees its slot for the next one.
    while (found.size < this.limit && i < handles.length && !signal.aborted) {
      const batch = handles.slice(i, i + (this.limit - found.size));
      i += batch.length;
      await Promise.all(
        batch.map(async (h) => {
          const hit = cache.items[h];
          if (hit && typeof hit.h === 'string' && now - hit.t < CACHE_TTL) {
            found.set(h, hit.h);
            return;
          }
          const res = await this.fetchCard(h, signal);
          if (res && res.html) {
            found.set(h, res.html);
            cache.items[h] = { t: Date.now(), h: res.html };
          } else if (res && res.gone) {
            gone.push(h);
          }
        })
      );
    }
    writeCache(cache);
    if (gone.length) writeList(readList().filter((h) => !gone.includes(h)));
    return handles.filter((h) => found.has(h)).slice(0, this.limit).map((h) => found.get(h));
  }

  async fetchCard(handle, signal) {
    const base = `${ROOT}products/${encodeURIComponent(handle)}`;
    try {
      if (this.sectionId) {
        const res = await fetch(`${base}?section_id=${encodeURIComponent(this.sectionId)}`, { signal, credentials: 'same-origin' });
        if (res.ok) {
          const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
          const tpl = doc.querySelector('template[data-fv-rv-card]');
          const card = tpl && tpl.content.querySelector('.fv-card');
          if (card) return { html: card.outerHTML };
        }
      }
      const res = await fetch(`${base}.js`, { signal, credentials: 'same-origin', headers: { Accept: 'application/json' } });
      if (res.status === 404) return { gone: true };
      if (!res.ok) return null;
      return { html: buildCard(await res.json(), handle, this.money) };
    } catch (e) {
      return null; // offline or aborted: keep the handle, try again on the next page
    }
  }

  render(cards) {
    cancelFades(this.$body);
    cancelFades(this.$cleared);
    this.classList.remove('is-cleared');
    this.$cleared.hidden = true;

    if (cards.length < MIN_SHOWN) {
      this.$list.replaceChildren();
      if (designMode() && this.$editor) {
        this.classList.add('is-empty');
        this.$editor.hidden = false;
        this.hidden = false;
      } else {
        this.hidden = true;
      }
      return;
    }

    this.classList.remove('is-empty');
    if (this.$editor) this.$editor.hidden = true;
    this.$list.innerHTML = cards.join('');
    Array.from(this.$list.children).forEach((li, i) => li.style.setProperty('--fv-rv-i', String(i)));
    this.$list.scrollLeft = 0;

    const firstShow = this.hidden;
    if (firstShow) this.prepareEntrance();
    this.hidden = false;
    this.updateRail();
    if (firstShow) this.playEntrance();
  }

  /* Header and cards rise in once the rail actually scrolls into view (not while off screen). */
  prepareEntrance() {
    this._motion = !reduceMotion() && !designMode() && 'IntersectionObserver' in window;
    if (this._motion) this.classList.add('is-anim');
  }

  playEntrance() {
    if (!this._motion) return;
    if (this.io) this.io.disconnect();
    let reported = false;
    this.io = new IntersectionObserver(
      (entries) => {
        reported = true;
        if (entries.some((e) => e.isIntersecting)) {
          this.classList.add('is-in');
          this.io.disconnect();
        }
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0.1 }
    );
    this.io.observe(this.$list);
    // Hidden documents / embedded previews may never report: show everything rather than nothing.
    setTimeout(() => {
      if (!reported || document.hidden) this.classList.add('is-in');
    }, 1500);
  }

  updateRail() {
    if (this.hidden || !this.$list) return;
    const g = this.$list;
    const max = g.scrollWidth - g.clientWidth;
    const over = max > 2;
    this.$nav.hidden = !over;
    this.$bar.hidden = !over;
    if (!over) return;
    const x = Math.min(1, Math.max(0, Math.abs(g.scrollLeft) / max));
    const prev = this.$nav.querySelector('[data-fv-rv-step="-1"]');
    const next = this.$nav.querySelector('[data-fv-rv-step="1"]');
    if (prev) prev.disabled = x <= 0.01;
    if (next) next.disabled = x >= 0.99;
    const visible = g.clientWidth / g.scrollWidth;
    const track = this.$bar.clientWidth;
    this.$thumb.style.width = `${(visible * 100).toFixed(3)}%`;
    this.$thumb.style.transform = `translateX(${(x * (1 - visible) * track).toFixed(1)}px)`;
  }

  step(dir) {
    const g = this.$list;
    const rtl = getComputedStyle(g).direction === 'rtl';
    g.scrollBy({ left: dir * (rtl ? -1 : 1) * g.clientWidth, behavior: reduceMotion() ? 'auto' : 'smooth' });
  }

  async clear() {
    if (this._busy) return;
    this._busy = true;
    this.saved = readList();
    writeList([]);
    removeKey('sessionStorage', CACHE_KEY);
    await fade(this.$body, 1, 0, 220);
    this.classList.add('is-cleared');
    this.$cleared.hidden = false;
    fade(this.$cleared, 0, 1, 360);
    this.announce('Recently viewed cleared from this device.');
    if (this.$undo) this.$undo.focus({ preventScroll: true });
    this._busy = false;
  }

  undo() {
    const now = readList();
    writeList([...now, ...(this.saved || []).filter((h) => !now.includes(h))]);
    this.saved = null;
    cancelFades(this.$body);
    cancelFades(this.$cleared);
    this.$cleared.hidden = true;
    this.classList.remove('is-cleared');
    fade(this.$body, 0, 1, 360);
    this.updateRail();
    this.announce('Recently viewed restored.');
    if (this.$clear) this.$clear.focus({ preventScroll: true });
  }

  announce(text) {
    if (!this.$status) return;
    this.$status.textContent = '';
    requestAnimationFrame(() => {
      this.$status.textContent = text;
    });
  }
}

if (!customElements.get('fv-recently-viewed')) customElements.define('fv-recently-viewed', FvRecentlyViewed);
